'use client';

import classNames from 'classnames';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import React from 'react';
import { InfiniteData } from 'react-query';
import Logo from '@/components/atoms/logo/logo';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import { CheckboxProps } from '@/components/molecules/checkbox/checkbox';
import DualChoice from '@/components/molecules/dual-choice/dual-choice';
import { RadiobuttonProps } from '@/components/molecules/radiobutton/radiobutton';
import { RangeSliderProps } from '@/components/molecules/range-slider/range-slider';
import Select, { SelectOption } from '@/components/molecules/select/select';
import FilterButtonList, {
  FilterButtonItem,
} from '@/components/organisms/filter-button-list/filter-button-list';
import FilterChipList, {
  FilterChip,
} from '@/components/organisms/filter-chip-list/filter-chip-list';
import FilterSlideIn from '@/components/organisms/filter-slide-in/filter-slide-in';
import {
  DualChoiceSwitch,
  Filter,
  FilterInput,
} from '@/components/organisms/filter-slide-in/filter-slide-in-input-group/filter-slide-in-input-group';
import { SliderImageItem } from '@/components/organisms/image-slider/image-slider';
import ProductCard, {
  ProductCardProps,
} from '@/components/organisms/product-card/product-card';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Row from '@/components/utilities/row/row';
import useQueryCampaignsHotelsWithOffers from '@/integrations/offers/campaigns/hotels-with-offers/hotels-with-offers.hooks';
import { useFavoritesContext } from '@/libraries/contexts/favorites-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { useSearchContext } from '@/libraries/contexts/search-context';
import {
  campaignPageEvent$,
  CampaignPageEventType,
} from '@/libraries/events/page-events';
import { updateUrlWithParameters } from '@/libraries/formatters/update-url-with-parameters';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import { getCampaignSortBy } from '@/libraries/getters/get-campaign-sort-by';
import { getLastTwoWords } from '@/libraries/getters/get-last-two-words';
import getLocale from '@/libraries/getters/get-locale';
import { getLogoName } from '@/libraries/getters/get-logo-name';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import isDeepEqual from '@/libraries/utils/is-deep-equal';
import {
  FILTER_PARAMETER_KEYS,
  formatFilterDataForSlideIn,
  hasFilterParameters,
  parseFilters,
  withLatestSliderRange,
} from '@/libraries/utils/search-results';
import styles from './campaign-page-results.module.scss';

const SEARCH_RESULTS_ID = 'search-results';

export interface CampaignPageResultsProps {
  /** Search settings for filters and search bar */
  searchSettings: Content.Components['SearchSettingsDto'];
}

export type FilterChangeTriggerType =
  | null
  | 'userAction'
  | 'init'
  | 'priceDualChoice';

const CampaignPageResults = ({ searchSettings }: CampaignPageResultsProps) => {
  const isTourpaqSearch = searchSettings.useTourpaqSearch;
  const { t } = useI18n();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isLaptop = useWindowSize('laptop');
  const isTablet = useWindowSize('tablet');
  const { brand } = usePageDetailsContext();
  const isMountedRef = useRef(false);
  const currencyFormatter = useCurrencyFormatter();
  const [pricePerPersonDualChoice, setPricePerPersonDualChoice] = useState(
    searchSettings.totalPricePerPerson ?? false,
  );
  const [showFilterSlideIn, setShowFilterSlideIn] = useState(false);
  const [sorting, setSorting] = useState<Content.Properties.SortingOptions>(
    getCampaignSortBy(searchSettings),
  );
  const [isFirstPage, setIsFirstPage] = useState(true);
  const [filterButtons, setFilterButtons] = useState<FilterButtonItem[]>([]);
  const [filterChips, setFilterChips] = useState<FilterChip[]>([]);
  const [filters, setFilters] = useState<Offers.Properties.SearchFilters>([]);
  const [slideInFilters, setSlideInFilters] = useState<Filter[]>([]);
  const [scrollTargetId, setScrollTargetId] = useState<string>('');
  const [filterChangeTrigger, setFilterChangeTrigger] =
    useState<FilterChangeTriggerType>(null);
  const [formattedResults, setFormattedResults] = useState<ProductCardProps[]>(
    [],
  );
  const {
    adultsNo,
    adultsNoPerRoom,
    automaticRoomAssignment,
    childrenAges,
    childrenAgesPerRoom,
    departureDate,
    departureIds,
    destinationIds,
    destinationRegionIds,
    hotelIds,
    maxDepartureDate,
    periodType,
    setTriggeredBy,
    triggeredBy,
  } = useSearchContext();
  const locale = getLocale();

  const sortingOptions: SelectOption[] = [
    { name: t('items.search.filters.departureDate'), value: 'departureDate' },
    { name: t('items.search.filters.discountAsc'), value: 'discountAscending' },
    {
      name: t('items.search.filters.discountDesc'),
      value: 'discountDescending',
    },
    { name: t('items.search.filters.popular'), value: 'websitePriority' },
    {
      name: t('items.search.filters.alphabetical'),
      value: 'hotelNameAscending',
    },
    { name: t('items.search.filters.ratingAsc'), value: 'starsAscending' },
    { name: t('items.search.filters.ratingDesc'), value: 'starsDescending' },
    { name: t('items.search.filters.priceAsc'), value: 'priceAscending' },
    { name: t('items.search.filters.priceDesc'), value: 'priceDescending' },
  ];

  // Tourpaq campaigns sort by the order set in the CMS, so sorting is hidden
  const showSorting = isLaptop && !isTourpaqSearch;

  const { addFavoriteOffer, favoriteOffers, isFavorite, removeFavoriteOffer } =
    useFavoritesContext();

  const {
    data: dataHotelsWithOffers,
    fetchNextPage: fetchNextPageHotelsWithOffers,
    hasNextPage: hasNextPageHotelsWithOffers,
    isFetched: isFetchedHotelsWithOffers,
    isFetching: isFetchingHotelsWithOffers,
    isFetchingNextPage: isFetchingNextPageHotelsWithOffers,
  } = useQueryCampaignsHotelsWithOffers({
    brand: brand,
    campaignId: searchSettings.campaignId?.toString() ?? '',
  });

  const totalResults = dataHotelsWithOffers?.pages[0]?.pagination.count || 0;
  const availableResults =
    dataHotelsWithOffers?.pages[0]?.pagination.availableCount || 0;
  const take = dataHotelsWithOffers?.pages[0]?.pagination.take || 0;
  const loadedResults =
    dataHotelsWithOffers?.pages.reduce(
      (acc, page) => acc + (page?.result?.length || 0),
      0,
    ) ?? 0;
  const remainingResults = totalResults - loadedResults;
  const resultsToLoad = Math.min(take, remainingResults);
  const noResults = isFetchedHotelsWithOffers && totalResults === 0;
  const hasFilters = hasFilterParameters(searchParams);

  useEffect(() => {
    if (isTablet) return;

    if (!isFirstPage) return;

    if (formattedResults.length === 0) return;

    // Prevents scrolling to the results on initial page load
    if (!isMountedRef.current) {
      isMountedRef.current = true;

      return;
    }

    const targetElement = document.getElementById(SEARCH_RESULTS_ID);

    if (!targetElement) return;

    const targetRect = targetElement.getBoundingClientRect();
    const targetTop = targetRect.top + window.scrollY;
    const currentScroll = window.scrollY;

    // If target is below the current scroll position; scroll down.
    // Prevents scrolling up when user navigates back to a scrolled search result
    if (targetTop < currentScroll) return;

    targetElement.scrollIntoView({ behavior: 'smooth' });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedResults]);

  const handleChangeSlider = useCallback(
    (newValue: number, filterId: string) => {
      setSlideInFilters(prevFilters => {
        const filterIndex = prevFilters.findIndex(
          filter => filter.id === filterId,
        );

        if (filterIndex === -1) return prevFilters;

        const updatedFilters = [...prevFilters];

        if (updatedFilters[filterIndex].type === FilterInput.SLIDER) {
          const updatedOptions = [
            ...updatedFilters[filterIndex].options,
          ] as RangeSliderProps[];

          updatedOptions[0] = {
            ...updatedOptions[0],
            value: newValue,
          };

          updatedFilters[filterIndex] = {
            ...updatedFilters[filterIndex],
            options: updatedOptions as RangeSliderProps[],
          };
        }

        return updatedFilters;
      });

      setFilterChangeTrigger('userAction');
    },
    [],
  );

  // change price dual choice from filer slide in
  const handleChangeDualChoice = useCallback(
    (filterId: string) => {
      setSlideInFilters(prevFilters => {
        return prevFilters.map(filter => {
          if (filter.id === filterId && filter.type === FilterInput.SLIDER) {
            const newCheckedState = !(filter.label as DualChoiceSwitch)
              .dualChoiceProps.checked;

            return {
              ...filter,
              label: {
                ...(filter.label as DualChoiceSwitch),
                dualChoiceProps: {
                  ...(filter.label as DualChoiceSwitch).dualChoiceProps,
                  checked: newCheckedState,
                },
              },
            };
          }

          return filter;
        });
      });

      setPricePerPersonDualChoice(previousValue => !previousValue);
      setFilterChangeTrigger('priceDualChoice');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pricePerPersonDualChoice],
  );

  const handleChangeCheckbox = useCallback(
    (checked: boolean, filterId: string, optionIndex: number) => {
      setSlideInFilters(prevFilters => {
        const filterIndex = prevFilters.findIndex(
          filter => filter.id === filterId,
        );

        if (filterIndex === -1) return prevFilters;

        const updatedFilters = [...prevFilters];

        if (
          updatedFilters[filterIndex].type === FilterInput.CHECKBOX ||
          updatedFilters[filterIndex].type === FilterInput.SINGLE_CHECKBOX
        ) {
          const updatedOptions = [
            ...updatedFilters[filterIndex].options,
          ] as CheckboxProps[];

          updatedOptions[optionIndex] = {
            ...updatedOptions[optionIndex],
            checked: checked,
          };

          updatedFilters[filterIndex] = {
            ...updatedFilters[filterIndex],
            options: updatedOptions as CheckboxProps[],
          };
        }

        return updatedFilters;
      });

      setFilterChangeTrigger('userAction');
    },
    [],
  );

  const handleChangeRadioButton = useCallback(
    (checked: boolean, filterId: string, optionIndex: number) => {
      setSlideInFilters(prevFilters => {
        const filterIndex = prevFilters.findIndex(
          filter => filter.id === filterId,
        );

        if (filterIndex === -1) return prevFilters;

        const updatedFilters = [...prevFilters];

        if (updatedFilters[filterIndex].type === FilterInput.RADIO) {
          const updatedOptions = updatedFilters[filterIndex].options.map(
            (option, index) => ({
              ...option,
              checked: index === optionIndex ? checked : false,
            }),
          );

          updatedFilters[filterIndex] = {
            ...updatedFilters[filterIndex],
            options: updatedOptions as RadiobuttonProps[],
          };
        }

        return updatedFilters;
      });

      setFilterChangeTrigger('userAction');
    },
    [],
  );

  const handleClickFavorite = ({
    isFavorite,
    offerId,
  }: {
    isFavorite: boolean;
    offerId: string;
  }) => {
    if (isFavorite) {
      removeFavoriteOffer(offerId);
    } else {
      addFavoriteOffer(offerId);
    }
  };

  const formatSearchResults = useCallback(
    (
      searchResult: InfiniteData<Offers.Endpoints.CampaignHotelsWithOffers>,
    ): ProductCardProps[] => {
      if (!searchResult) return [];

      return searchResult.pages.flatMap(page =>
        page.result
          ? page.result.map(
              (item: Offers.Properties.SearchResult): ProductCardProps => {
                const images: SliderImageItem[] = item.pictures.map(
                  picture => ({
                    altText: picture.description || '',
                    url: picture.url,
                  }),
                );

                const tags = item.facilityHighlights || [];
                const imageTags = item.facilityTags || [];

                const bestOfferSummary = item.bestOfferSummary;

                const bestOfferTotalPrice = pricePerPersonDualChoice
                  ? bestOfferSummary?.price.totalPricePerPerson
                  : bestOfferSummary?.price.totalPrice;
                const bestOfferNormalPrice = pricePerPersonDualChoice
                  ? bestOfferSummary?.price.normalPricePerPerson
                  : bestOfferSummary?.price.normalPrice;
                const bestOfferDiscountPrice = pricePerPersonDualChoice
                  ? bestOfferSummary?.price.discountPerPerson
                  : bestOfferSummary?.price.discount;

                const price = currencyFormatter(bestOfferTotalPrice as number);
                const offerAmount = currencyFormatter(
                  bestOfferNormalPrice as number,
                );
                const offerDiscount = currencyFormatter(
                  bestOfferDiscountPrice as number,
                );

                let priceLabelAmount: string;

                const adultsLabel =
                  bestOfferSummary?.adultsNo === 1
                    ? t('items.common.words.adult')
                    : t('items.common.words.adults');
                const childrenLabel =
                  bestOfferSummary?.childrenNo === 1
                    ? t('items.common.words.child')
                    : t('items.common.words.children');

                if (
                  bestOfferSummary?.childrenNo &&
                  bestOfferSummary?.childrenNo > 0
                ) {
                  priceLabelAmount = `${bestOfferSummary?.adultsNo} ${adultsLabel}, ${bestOfferSummary?.childrenNo} ${childrenLabel}`;
                } else {
                  priceLabelAmount = `${bestOfferSummary?.adultsNo} ${adultsLabel}`;
                }

                const priceLabel = pricePerPersonDualChoice
                  ? t('items.search.productCard.pricePerPerson', {
                      persons: priceLabelAmount,
                    })
                  : t('items.search.productCard.totalPrice', {
                      persons: priceLabelAmount,
                    });

                const discount =
                  bestOfferSummary?.price.discount !== 0 ? offerDiscount : '0';

                const transportMode = item.bestOfferSummary?.transportMode;

                const isFavoriteOffer = isFavorite(
                  item.bestOfferSummary?.uniqueId as string,
                );

                return {
                  additionalTag: bestOfferSummary?.additionalTag,
                  departureName: bestOfferSummary?.departureName ?? undefined,
                  description: bestOfferSummary?.description,
                  duration: `${bestOfferSummary?.daysDuration.toString()} ${t('items.search.productCard.nights')}`,
                  highlightScheduled:
                    bestOfferSummary?.highlightScheduled ?? undefined,
                  homeboundDepartureDate:
                    bestOfferSummary?.homeboundDepartureDate ?? undefined,
                  homeboundDepartureName:
                    bestOfferSummary?.homeboundDepartureName ?? undefined,
                  hotelName: item.hotelName,
                  images,
                  imageTags,
                  includedProducts: item.bestOfferSummary?.includedProducts,
                  isFavorite: isFavoriteOffer,
                  location: item.location,
                  middleSection: bestOfferSummary?.middleSection,
                  offerAmount: discount !== '0' ? offerAmount : '',
                  offerId: item.id,
                  offerTag: '',
                  onClickFavorite: () =>
                    handleClickFavorite({
                      isFavorite: isFavoriteOffer,
                      offerId: item.bestOfferSummary?.uniqueId as string,
                    }),
                  outboundArrivalDate:
                    bestOfferSummary?.outboundArrivalDate ?? undefined,
                  outboundDepartureDate:
                    bestOfferSummary?.outboundDepartureDate ?? undefined,
                  outboundDepartureName:
                    bestOfferSummary?.outboundDepartureName ?? undefined,
                  price:
                    price ??
                    item.roomConfigurations[0].offers[0].price.totalPrice.toString(),
                  priceDetails: item.bestOfferSummary
                    ?.detailsModal as Content.Components['PriceDetailsDto'],
                  priceDiscount:
                    discount !== '0'
                      ? `${t('items.search.productCard.save')} ${discount}`
                      : '0',
                  priceLabel,
                  productCardType: item.productCardType,
                  rating: item.stars as number,
                  showHeartHotelLogo: item.isHeartHotel,
                  specialHotelType: item.specialHotelType,
                  tags,
                  target: item.bestOfferSummary?.link?.target,
                  transportMode,
                  tripHighlights: item.tripHighlights,
                  tripSummary: item.tripSummary,
                  uniqueId: item.bestOfferSummary?.uniqueId,
                  urgencyTags: item.bestOfferSummary?.urgencyTags,
                  url: item.bestOfferSummary?.searchUrl ?? item.url,
                };
              },
            )
          : [],
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFavorite, pricePerPersonDualChoice, locale],
  );

  // build search parameters
  const buildSearchParameters = (
    overrides: Partial<Record<string, any>> = {},
  ): Record<string, any> => {
    const searchParameters: Record<string, any> = {
      batchSize: searchSettings.batchSize,
      durationType: periodType,
      sortBy: overrides.sortBy || sorting,
      ...overrides,
    };

    if (
      (departureIds !== '' && departureIds !== undefined) ||
      overrides.departureIds
    ) {
      searchParameters.departureIds = overrides.departureIds || departureIds;
    }

    if (
      (destinationIds !== '' && destinationIds !== undefined) ||
      overrides.destinationIds
    ) {
      searchParameters.destinationIds =
        overrides.destinationIds || destinationIds;
    }

    if (
      (destinationRegionIds !== '' && destinationRegionIds !== undefined) ||
      overrides.destinationRegionIds
    ) {
      searchParameters.destinationRegionIds =
        overrides.destinationRegionIds || destinationRegionIds;
    }

    if (
      (departureDate !== '' && departureDate !== undefined) ||
      overrides.departureDate
    ) {
      searchParameters.departureDate = overrides.departureDate || departureDate;
    }

    if ((hotelIds !== '' && hotelIds !== undefined) || overrides.hotelIds) {
      searchParameters.hotelIds = overrides.hotelIds || hotelIds;
    }

    if (
      (maxDepartureDate !== '' && maxDepartureDate !== undefined) ||
      overrides.maxDepartureDate
    ) {
      searchParameters.maxDepartureDate =
        overrides.maxDepartureDate || maxDepartureDate;
    }

    if (
      automaticRoomAssignment &&
      (adultsNo !== '' || overrides.adultsNumber)
    ) {
      searchParameters.childrenAges = overrides.childrenAges || childrenAges;
      searchParameters.adultsNumber = overrides.adultsNumber || adultsNo;
    } else if (adultsNoPerRoom !== '' || overrides.adultsNoPerRoom) {
      searchParameters.adultsPerRoom =
        overrides.adultsNoPerRoom || adultsNoPerRoom;
      searchParameters.childrenAgesPerRoom =
        overrides.childrenAgesPerRoom || childrenAgesPerRoom;
    }

    return searchParameters;
  };

  // update url with given parameters
  const updateSearchUrl = ({
    filterParameters,
    searchParameters,
    shouldOverwriteUrl = false,
  }: {
    filterParameters: Record<string, any>;
    searchParameters: Record<string, any>;
    shouldOverwriteUrl?: boolean;
  }) => {
    if (periodType !== '') {
      updateUrlWithParameters({
        newParameters: { ...filterParameters, ...searchParameters },
        overwrite: shouldOverwriteUrl,
        router,
      });
    }
  };

  const updateSearchUrlWithParamsAndFilters = (
    overrides: Partial<Record<string, any>> = {},
    shouldOverwriteUrl: boolean = false,
  ) => {
    const searchParameters = buildSearchParameters(overrides);
    const filterParameters = parseFilters(
      slideInFilters,
      pricePerPersonDualChoice,
      adultsNo,
    );

    updateSearchUrl({ filterParameters, searchParameters, shouldOverwriteUrl });
  };

  // set filters from searchSettings from search page
  useEffect(() => {
    setFilters(previousFilters => {
      const nextFilters = searchSettings?.filters ?? [];

      return isDeepEqual(previousFilters, nextFilters)
        ? previousFilters
        : nextFilters;
    });

    const filterButtonsList = searchSettings?.filters.map(item => ({
      count: undefined,
      disabled: false,
      id: item.id.toString(),
      label: item.name,
      showIcon: false,
    }));

    filterButtonsList?.unshift({
      count: undefined,
      disabled: false,
      id: '0',
      label: t('items.search.filters.filter'),
      showIcon: true,
    });

    // set filter buttons from searchSettings from search page
    setFilterButtons(filterButtonsList ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchSettings?.filters]);

  // set filters from search results and update filters with values
  useEffect(() => {
    const updateFiltersStateWithNewData = (initialState, newData) => {
      return initialState.map(initialItem => {
        const updatedItem = newData.find(
          newItem => newItem.id === initialItem.id,
        );

        if (updatedItem) {
          return withLatestSliderRange(
            {
              ...initialItem,
              ...updatedItem,
              facilities: updatedItem.facilities
                ? updatedItem.facilities
                : initialItem.facilities,
              facility: updatedItem.facility
                ? { ...initialItem.facility, ...updatedItem.facility }
                : initialItem.facility,
            },
            updatedItem,
          );
        }

        return withLatestSliderRange(initialItem);
      });
    };

    // reset filters from search results if new search is made
    if (triggeredBy === 'searchButton') {
      setFilters(prevFilters => {
        const nextFilters = updateFiltersStateWithNewData(
          prevFilters,
          dataHotelsWithOffers?.pages[0].filters,
        );

        return isDeepEqual(prevFilters, nextFilters)
          ? prevFilters
          : nextFilters;
      });

      setTriggeredBy('automaticTrigger');

      return;
    }

    if (filterChangeTrigger === 'userAction') return;

    if (!dataHotelsWithOffers?.pages[0].filters) return;

    setFilters(prevFilters => {
      const nextFilters = updateFiltersStateWithNewData(
        prevFilters,
        dataHotelsWithOffers?.pages[0].filters,
      );

      return isDeepEqual(prevFilters, nextFilters) ? prevFilters : nextFilters;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataHotelsWithOffers]);

  useEffect(() => {
    const subscription = campaignPageEvent$.subscribe(event => {
      if (event.type !== CampaignPageEventType.SEARCH) return;

      setIsFirstPage(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  // format filters to slideInFilters
  useEffect(() => {
    const nextSlideInFilters = formatFilterDataForSlideIn(filters, {
      currencyFormatter,
      handleChangeCheckbox,
      handleChangeDualChoice,
      handleChangeRadioButton,
      handleChangeSlider,
      pricePerPersonDualChoice,
      t,
    });

    setSlideInFilters(previousFilters => {
      return isDeepEqual(previousFilters, nextSlideInFilters)
        ? previousFilters
        : nextSlideInFilters;
    });
    setFilterChangeTrigger('init');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, locale]);

  // format filters to slideInFilters on price dual choice change
  useEffect(() => {
    const nextSlideInFilters = formatFilterDataForSlideIn(filters, {
      currencyFormatter,
      handleChangeCheckbox,
      handleChangeDualChoice,
      handleChangeRadioButton,
      handleChangeSlider,
      pricePerPersonDualChoice,
      t,
    });

    setSlideInFilters(previousFilters => {
      return isDeepEqual(previousFilters, nextSlideInFilters)
        ? previousFilters
        : nextSlideInFilters;
    });
    setFilterChangeTrigger('priceDualChoice');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricePerPersonDualChoice]);

  // format results to be displayed
  useEffect(() => {
    if (!dataHotelsWithOffers) return;

    const formattedSearchResults = formatSearchResults(dataHotelsWithOffers);

    // in order to align with the logic in search results page
    setFormattedResults(previous => {
      if (JSON.stringify(previous) === JSON.stringify(formattedSearchResults)) {
        return previous;
      }

      return formattedSearchResults;
    });
  }, [
    dataHotelsWithOffers,
    favoriteOffers,
    formatSearchResults,
    pricePerPersonDualChoice,
  ]);

  // url encode filters, execute search, update filter button count, generate filter chips
  useEffect(() => {
    const clearFilterParamsFromUrl = () => {
      const currentPath = window.location.pathname;
      const existingQueryString = window.location.search;

      if (existingQueryString !== '') {
        const existingParams = new URLSearchParams(existingQueryString);

        FILTER_PARAMETER_KEYS.forEach(key => {
          existingParams.delete(key);
        });

        const newUrl = `${currentPath}?${existingParams.toString()}`;

        window.history.replaceState(null, '', newUrl);
      }
    };

    const updateFilterButtonCounts = () => {
      setFilterButtons(prevButtons => {
        const updatedButtons = [...prevButtons];

        let totalCount = 0;

        slideInFilters.forEach(filter => {
          const filterButtonIndex = updatedButtons.findIndex(
            button => button.id === filter.id,
          );

          if (filterButtonIndex !== -1) {
            let count = 0;

            if (filter.type === FilterInput.SLIDER) {
              const sliderOption = filter.options[0];

              if (sliderOption.value !== sliderOption.maxValue) {
                count = 1;
              }
            } else if (filter.type === FilterInput.CHECKBOX) {
              count = (filter.options as CheckboxProps[]).filter(
                option => option.checked,
              ).length;
            } else if (filter.type === FilterInput.RADIO) {
              count = (filter.options as CheckboxProps[]).filter(
                option => option.checked && option.id !== null,
              ).length;
            } else if (
              filter.type === FilterInput.SINGLE_CHECKBOX &&
              (filter.name === 'DiscountFilterDto' ||
                filter.name === 'TransportHotelFilterDto') &&
              filter.options[0].checked
            ) {
              count = 1;
            }

            updatedButtons[filterButtonIndex] = {
              ...updatedButtons[filterButtonIndex],
              count: count === 0 ? undefined : count,
            };

            if (count > 0) {
              totalCount += count;
            }
          }
        });

        const buttonZeroIndex = updatedButtons.findIndex(
          button => button.id === '0',
        );

        if (buttonZeroIndex !== -1) {
          updatedButtons[buttonZeroIndex] = {
            ...updatedButtons[buttonZeroIndex],
            count: totalCount > 0 ? totalCount : undefined,
          };
        }

        return updatedButtons;
      });
    };

    const clearFilterButtonCounts = () => {
      setFilterButtons(prevButtons =>
        prevButtons.map(button => ({
          ...button,
          count: undefined,
        })),
      );
    };

    const generateFiltersChips = (): FilterChip[] => {
      return slideInFilters.flatMap(filter => {
        if (
          filter.type === FilterInput.CHECKBOX ||
          filter.type === FilterInput.RADIO
        ) {
          return (filter.options as CheckboxProps[])
            .filter(option => option.checked && option.id !== null)
            .map(option => ({
              id: option.value as string,
              label: option.label ?? '',
            }));
        }

        if (filter.type === FilterInput.SLIDER) {
          const sliderOption = filter.options[0];

          if (sliderOption.value !== sliderOption.maxValue) {
            let labelContent: string;

            if (filter.name === 'PriceFilterDto') {
              labelContent = `${currencyFormatter(sliderOption.value as number)}`;
            } else {
              labelContent = `${sliderOption.value} ${sliderOption.unit ? sliderOption.unit : ''} ${getLastTwoWords(filter.label as string)}`;
            }

            return [
              {
                id: filter.id,
                label: `${t('items.search.filters.upTo')} ${labelContent}`,
              },
            ];
          }
        }

        if (
          filter.type === FilterInput.SINGLE_CHECKBOX &&
          (filter.name === 'DiscountFilterDto' ||
            filter.name === 'TransportHotelFilterDto') &&
          filter.options[0].checked
        ) {
          return [
            {
              id: filter.id,
              label: filter.options[0].label ?? '',
            },
          ];
        }

        return [];
      });
    };

    const clearFilterChips = () => {
      setFilterChips([]);
    };

    const filterParameters = parseFilters(
      slideInFilters,
      pricePerPersonDualChoice,
      adultsNo,
    );
    const searchParameters = buildSearchParameters();

    if (filterChangeTrigger === 'priceDualChoice') return;

    if (Object.keys(filterParameters).length === 0) {
      // Only filters the user cleared leave the url, as the filters aren't
      // set up from it yet, and a linked search would lose its filters
      if (filterChangeTrigger === 'userAction') {
        clearFilterParamsFromUrl();
      }

      clearFilterChips();
      clearFilterButtonCounts();

      return;
    } else {
      setFilterChips(generateFiltersChips());
      updateFilterButtonCounts();
    }

    if (filterChangeTrigger === 'userAction') {
      updateSearchUrl({
        filterParameters,
        searchParameters,
        shouldOverwriteUrl: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideInFilters]);

  // results page price per person dual choice
  const handleClickPriceDualChoice = () => {
    setPricePerPersonDualChoice(previousValue => !previousValue);
    setFilterChangeTrigger('priceDualChoice');
  };

  const handleChangeSorting = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setTriggeredBy('filtering');

    setSorting(event.target.value as Content.Properties.SortingOptions);
    updateSearchUrlWithParamsAndFilters({ sortBy: event.target.value });
  };

  const handleClickFilterButton = (id: string) => {
    setShowFilterSlideIn(true);
    setScrollTargetId(id);
  };

  const handleClickRemoveAllChips = () => {
    setSlideInFilters(prevFilters => {
      return prevFilters.map(filter => {
        if (filter.type === FilterInput.SLIDER) {
          const updatedOptions = filter.options.map(option => ({
            ...option,
            value: option.maxValue,
          }));

          return { ...filter, options: updatedOptions };
        } else if (
          filter.type === FilterInput.CHECKBOX ||
          filter.type === FilterInput.RADIO ||
          filter.type === FilterInput.SINGLE_CHECKBOX
        ) {
          const updatedOptions = filter.options.map(option => ({
            ...option,
            checked: false,
          }));

          return { ...filter, options: updatedOptions };
        }

        return filter;
      });
    });

    setFilterChangeTrigger('userAction');
  };

  const handleClickRemoveChip = (chipToRemove: FilterChip) => {
    setSlideInFilters(prevFilters => {
      return prevFilters.map(filter => {
        if (
          filter.type === FilterInput.SLIDER &&
          filter.id === chipToRemove.id
        ) {
          const updatedOptions = filter.options.map(option => ({
            ...option,
            value: option.maxValue,
          }));

          return { ...filter, options: updatedOptions };
        } else if (
          (filter.type === FilterInput.CHECKBOX ||
            filter.type === FilterInput.RADIO) &&
          filter.options.some(option => option.value === chipToRemove.id)
        ) {
          const updatedOptions = filter.options.map(option =>
            option.value === chipToRemove.id
              ? { ...option, checked: false }
              : option,
          );

          return { ...filter, options: updatedOptions };
        } else if (filter.type === FilterInput.SINGLE_CHECKBOX) {
          const updatedOptions = filter.options.map(option => ({
            ...option,
            checked: false,
          }));

          return { ...filter, options: updatedOptions };
        }

        return filter;
      });
    });

    setFilterChangeTrigger('userAction');
  };

  const handleClickShowMore = () => {
    setIsFirstPage(false);
    fetchNextPageHotelsWithOffers();
  };

  const renderNoResults = () => {
    // The filters may be what leaves out the results, so a filtered search
    // says so and offers to clear them
    const header = hasFilters
      ? t('items.search.results.noFilteredResults.header')
      : t('items.campaign.results.noResults.header');
    const text = hasFilters
      ? t('items.search.results.noFilteredResults.text')
      : t('items.campaign.results.noResults.text');

    return (
      <div className={styles.noResultsContainer}>
        <Logo
          className={styles.noResultsIcon}
          name={getLogoName({
            brand: brand,
            type: 'NoResults',
          })}
        />

        <div className={styles.noResultsContent}>
          <Typography
            className={styles.noResultsHeading}
            tag="h2"
            tagStyle="headingMedium"
          >
            {header}
          </Typography>

          <Typography
            className={styles.noResultsText}
            tag="span"
            tagStyle="bodyMedium"
          >
            {text}
          </Typography>

          {hasFilters && (
            <Button
              className={styles.noResultsButton}
              onClick={handleClickRemoveAllChips}
              text={t('items.search.filters.clearAllFilters')}
              variant="secondary"
            />
          )}
        </div>
      </div>
    );
  };

  const handleOnClickClear = () => {
    handleClickRemoveAllChips();
  };

  const handleOnClickViewResults = () => {
    setShowFilterSlideIn(false);
  };

  return (
    <React.Fragment>
      <section className={classNames(styles.component)}>
        <Container>
          <Row>
            <Column>
              <div className={styles.filterAndResultsContainer}>
                {isFetchingHotelsWithOffers &&
                filterChangeTrigger !== 'userAction' ? (
                  <div className={styles.filterContainer}>
                    <div className={styles.filterButtons}>
                      <SkeletonLoader
                        borderRadius="4px"
                        height="50px"
                        width="80%"
                      />
                    </div>

                    <div className={styles.sortingContainer}>
                      <div className={styles.sorting}>
                        <SkeletonLoader
                          borderRadius="4px"
                          height="50px"
                          width="40%"
                        />

                        {showSorting && (
                          <SkeletonLoader
                            borderRadius="4px"
                            height="50px"
                            width="40%"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  (formattedResults.length > 0 || hasFilters) && (
                    <div className={styles.filterContainer}>
                      <div className={styles.filterButtons}>
                        <FilterButtonList
                          items={isLaptop ? filterButtons : [filterButtons[0]]}
                          onClick={handleClickFilterButton}
                        />

                        {isLaptop && (
                          <FilterChipList
                            clearLabel={t(
                              'items.search.filters.clearAllFilters',
                            )}
                            items={filterChips}
                            onClickRemoveAll={handleClickRemoveAllChips}
                            onClickRemoveChip={handleClickRemoveChip}
                          />
                        )}
                      </div>

                      <div className={styles.sortingContainer}>
                        <div className={styles.sorting}>
                          <DualChoice
                            checked={pricePerPersonDualChoice}
                            labelLeft={t('items.common.words.totalPrice')}
                            labelRight={t('items.common.words.pricePerPerson')}
                            onClick={() => handleClickPriceDualChoice()}
                          />

                          {showSorting && (
                            <Select
                              label={t('items.search.filters.sorting')}
                              onChange={handleChangeSorting}
                              options={sortingOptions}
                              value={sorting}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )
                )}

                {!isFetchingHotelsWithOffers && noResults && renderNoResults()}

                {isFetchingHotelsWithOffers &&
                !isFetchingNextPageHotelsWithOffers ? (
                  <div className={styles.resultsContainer}>
                    <SkeletonLoader
                      borderRadius="2px"
                      height="25px"
                      width="30%"
                    />

                    <div className={styles.resultsList}>
                      {Array.from({ length: 4 }).map((_, loaderIndex) => (
                        <ProductCard key={loaderIndex} isLoading />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={styles.resultsContainer}>
                    {formattedResults.length > 0 &&
                      dataHotelsWithOffers?.pages[0].pagination && (
                        <React.Fragment>
                          <Typography
                            className={styles.heading}
                            id={SEARCH_RESULTS_ID}
                            tag="h2"
                            tagStyle="headingSmall"
                          >
                            {/* Tourpaq: the number of results is unknown */}
                            {isTourpaqSearch
                              ? t('items.search.results.headingWithoutCount')
                              : `${availableResults} ${t('items.search.results.heading')}`}
                          </Typography>

                          <div className={styles.resultsList}>
                            {formattedResults.map((result, resultIndex) => (
                              <ProductCard
                                key={result.uniqueId}
                                {...result}
                                priority={resultIndex === 0}
                              />
                            ))}
                          </div>
                        </React.Fragment>
                      )}

                    {isFetchingNextPageHotelsWithOffers && (
                      <div className={styles.resultsList}>
                        {Array.from({ length: 4 }).map((_, loaderIndex) => (
                          <ProductCard key={loaderIndex} isLoading />
                        ))}
                      </div>
                    )}

                    {hasNextPageHotelsWithOffers && (
                      <div className={styles.buttonContainer}>
                        <Button
                          disabled={
                            !hasNextPageHotelsWithOffers ||
                            isFetchingNextPageHotelsWithOffers
                          }
                          onClick={handleClickShowMore}
                          text={
                            isFetchingNextPageHotelsWithOffers
                              ? t('items.search.results.loadingResults')
                              : isTourpaqSearch
                                ? t('items.search.results.showMoreWithoutCount')
                                : t('items.search.results.showMore', {
                                    number: resultsToLoad,
                                  })
                          }
                          variant="secondary"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Column>
          </Row>
        </Container>
      </section>

      <FilterSlideIn
        activeFilters={{
          items: filterChips,
          onClickRemoveAll: handleClickRemoveAllChips,
          onClickRemoveChip: handleClickRemoveChip,
        }}
        filters={slideInFilters}
        headline={t('items.search.filterSlideIn.filters')}
        isOpen={showFilterSlideIn}
        onClickClear={handleOnClickClear}
        onClickViewResults={handleOnClickViewResults}
        scrollTargetId={scrollTargetId}
        setIsOpen={setShowFilterSlideIn}
      />
    </React.Fragment>
  );
};

CampaignPageResults.displayName = 'CampaignPageResults';

export default CampaignPageResults;
